/**
 * Jedyny stan radia.
 *
 * Wszystko, co pokazuje interfejs (NowPlaying, historia, miniplayer, tytuł
 * karty) oraz systemowy Media Session, czyta z tego store'u. Silniki
 * odtwarzania tylko go aktualizują - nie ma osobnego stanu „dla audio".
 */

import { create } from 'zustand';

import type { BroadcastItem, RadioStatus, UpcomingItem } from './radio.types';

const VOLUME_STORAGE_KEY = 'radio-adamowo:volume';
const HISTORY_STORAGE_KEY = 'radio-adamowo:history';
const DEFAULT_VOLUME = 0.8;
export const HISTORY_LIMIT = 50;

const clamp = (value: number): number => Math.min(1, Math.max(0, value));

function readStoredVolume(): number {
  try {
    const stored = window.localStorage.getItem(VOLUME_STORAGE_KEY);
    const parsed = stored === null ? Number.NaN : Number(stored);
    return Number.isFinite(parsed) ? clamp(parsed) : DEFAULT_VOLUME;
  } catch {
    return DEFAULT_VOLUME;
  }
}

function storeVolume(volume: number): void {
  try {
    window.localStorage.setItem(VOLUME_STORAGE_KEY, String(volume));
  } catch {
    // Brak dostępu do localStorage (tryb prywatny) nie może blokować playera.
  }
}

/** Historia żyje tyle, co sesja karty: przetrwa odświeżenie strony, nie przetrwa zamknięcia. */
function readStoredHistory(): BroadcastItem[] {
  try {
    const parsed: unknown = JSON.parse(window.sessionStorage.getItem(HISTORY_STORAGE_KEY) ?? '[]');
    return Array.isArray(parsed) ? (parsed as BroadcastItem[]).slice(0, HISTORY_LIMIT) : [];
  } catch {
    return [];
  }
}

function storeHistory(history: BroadcastItem[]): void {
  try {
    window.sessionStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
  } catch {
    // jw.
  }
}

export interface RadioState {
  status: RadioStatus;
  volume: number;
  muted: boolean;
  /** Pozycja na antenie - jedno źródło prawdy o tym, co gra. */
  currentItem: BroadcastItem | null;
  nextItem: UpcomingItem | null;
  /** Faktycznie wyemitowane pozycje, od najnowszej. */
  history: BroadcastItem[];
  /** Pozycja odtwarzania w sekundach (tryb demo); `null`, gdy nieznana. */
  position: number | null;
  /** Nazwa programu/pasma podana przez serwer (tryb stream). */
  program: string | null;

  setStatus: (status: RadioStatus) => void;
  setVolume: (volume: number) => void;
  toggleMuted: () => void;
  /**
   * Ustawia pozycję na antenie. Zmiana pozycji przenosi poprzednią do
   * historii (jeśli faktycznie grała); ta sama pozycja (`id`) jest tylko
   * uzupełniana o nowe dane.
   */
  setCurrentItem: (item: BroadcastItem | null) => void;
  /** Oznacza bieżącą pozycję jako faktycznie emitowaną (ustawia `startedAt`, jeśli brak). */
  markCurrentStarted: (at?: Date) => void;
  /** Uzupełnia dane bieżącej pozycji, np. długość odczytaną z pliku. */
  patchCurrentItem: (patch: Partial<Pick<BroadcastItem, 'duration' | 'artwork'>>) => void;
  setNextItem: (item: UpcomingItem | null) => void;
  setPosition: (position: number | null) => void;
  setHistory: (history: BroadcastItem[]) => void;
  setProgram: (program: string | null) => void;
}

export const useRadioStore = create<RadioState>((set) => ({
  status: 'paused',
  volume: readStoredVolume(),
  muted: false,
  currentItem: null,
  nextItem: null,
  history: readStoredHistory(),
  position: null,
  program: null,

  setStatus: (status) => set({ status }),
  setVolume: (volume) => {
    const next = clamp(volume);
    storeVolume(next);
    set({ volume: next, muted: false });
  },
  toggleMuted: () => set((state) => ({ muted: !state.muted })),

  setCurrentItem: (item) =>
    set((state) => {
      const previous = state.currentItem;

      if (previous && item && previous.id === item.id) {
        return { currentItem: { ...item, startedAt: item.startedAt ?? previous.startedAt } };
      }

      // Do historii trafia tylko to, co naprawdę zaczęło grać.
      if (previous?.startedAt) {
        const history = [previous, ...state.history].slice(0, HISTORY_LIMIT);
        storeHistory(history);
        return { currentItem: item, position: null, history };
      }

      return { currentItem: item, position: null };
    }),

  markCurrentStarted: (at = new Date()) =>
    set((state) =>
      state.currentItem && !state.currentItem.startedAt
        ? { currentItem: { ...state.currentItem, startedAt: at.toISOString() } }
        : state
    ),

  patchCurrentItem: (patch) =>
    set((state) =>
      state.currentItem ? { currentItem: { ...state.currentItem, ...patch } } : state
    ),

  setNextItem: (nextItem) => set({ nextItem }),
  setPosition: (position) => set({ position }),
  setHistory: (history) => {
    const next = history.slice(0, HISTORY_LIMIT);
    storeHistory(next);
    set({ history: next });
  },
  setProgram: (program) => set({ program }),
}));
